import React, {
  FormEvent,
  useCallback,
  useEffect,
  useState,
  useMemo,
} from "react";
import GoogleLogoIcon from "src/assets/icons/google_logo.svg?react";
import MSWindowsLogoIcon from "src/assets/icons/microsoft-window.svg?react";
import CleverLogoIcon from "src/assets/icons/clever.svg?react";
import CheckMarkIcon from "src/assets/icons/check_mark.svg?react";
import { useHistory } from "react-router-dom";
import styles from "./UserRegistrationForm.module.scss";
import { TextInput } from "@src/components/text-input/TextInput";
import {
  useGetInvitedUserQuery,
  useRegistrationMutation,
} from "@api/graphql-codegen";
import { hasError, generateRandomString } from "@src/utils/validation";
import { useAuthToken } from "@src/hooks/useAuthToken";
import { Events, useAnalytics } from "@src/hooks/useAnalytics";
import { useRegistrationForm, transformToAccountForm } from "./utils";
import useGoogleAuth from "@src/hooks/useGoogleAuth";
import useMicrosoftAuth from "@src/hooks/useMicrosoftAuth";
import useCleverAuth from "@src/hooks/useCleverAuth";
import ArrowIcon from "src/assets/icons/curvearrow.svg?react";
import RegistrationPageLayout from "@src/components/auth-page-layout/RegistrationPageLayout";
import Checkbox from "@src/components/checkbox/Checkbox";

export default function UserRegistration({ userId }: { userId: string }) {
  const { push } = useHistory();
  const { setAuthToken } = useAuthToken();
  const { segment, trackEvent, identify } = useAnalytics();
  const { form, formErrors, handleFieldChange } = useRegistrationForm("User");
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [isSocialAuthFlow, setSocialAuthFlow] = useState(false);
  const [unMatchedGoogleAccount, setUnMatchedGoogleAccount] = useState(false);
  const [unMatchedCleverAccount, setUnMatchedCleverAccount] = useState(false);
  const [unMatchedMicrosoftAccount, setUnMatchedMicrosoftAccount] =
    useState(false);

  const {
    data,
    loading: invitedUserLoading,
    error: invitedUserError,
  } = useGetInvitedUserQuery({ variables: { input: { id: userId } } });
  const { googleAuth, googleAuthUser, googleAuthLoading } =
    useGoogleAuth("Registration");
  const { microsoftAuth, microsoftUser, microsoftAuthLoading } =
    useMicrosoftAuth("Registration");
  const { cleverAuth, cleverUser, cleverAuthLoading } =
    useCleverAuth("Registration");

  useEffect(() => {
    if (
      invitedUserError &&
      invitedUserError.message.includes("Incorrect link")
    ) {
      push("/expiryRedirect");
    } else if (
      invitedUserError &&
      invitedUserError.message.includes("Already accepted")
    ) {
      push("/expiryRedirect?existing=true");
    }
  }, [invitedUserError, push]);

  useEffect(() => {
    if (data) {
      const invitedUser = data.invitedUser;
      if (invitedUser && !form.firstName) {
        handleFieldChange("firstName", invitedUser.firstName);
      }
      if (invitedUser && !form.lastName) {
        handleFieldChange("lastName", invitedUser.lastName);
      }
      if (invitedUser && !form.email) {
        handleFieldChange("email", invitedUser.email);
      }
    }
  }, [data, handleFieldChange, form.firstName, form.lastName, form.email]);

  const [registration, { loading: submitting, error: registrationError }] =
    useRegistrationMutation({
      onCompleted(data) {
        if (data?.registration.token) {
          setAuthToken(data?.registration.token);
          if (data?.registration.user?.id) {
            segment?.alias(data?.registration.user?.id);
            segment?.identify(data?.registration.user?.id, {
              $user_id: data?.registration.user?.id,
              $email: data?.registration.user?.email,
              school_id: (data?.registration.user?.schools || [])
                .map((s) => s?.id)
                .join(" "),
              school_name: (data?.registration.user?.schools || [])
                .map((s) => s?.name)
                .join(" "),
              customer: (data?.registration.user?.schools || [])
                .map((s) => s?.name)
                .join(" "),
              account: data?.registration.user?.customer?.name,
              account_id: data?.registration.user?.customer?.id,
              name: data?.registration.user?.firstName,
              fullName: `${data?.registration.user?.firstName || ""} ${
                data?.registration.user?.lastName || ""
              }`,
              createdAt: data?.registration.user?.createdAt,
            });
            const d = { email: data?.registration.user?.email, loginType: 'Email', userId: data.registration.user?.id };
            trackEvent(Events.actions.logIn, d);
            identify(data?.registration.user?.id, {
              $user_id: data?.registration.user?.id,
              $email: data?.registration.user?.email,
              school_id: (data?.registration.user?.schools || [])
                .map((s) => s?.id)
                .join(" "),
              school_name: (data?.registration.user?.schools || [])
                .map((s) => s?.name)
                .join(" "),
              customer: (data?.registration.user?.schools || [])
                .map((s) => s?.name)
                .join(" "),
              account: data?.registration.user?.customer?.name,
              account_id: data?.registration.user?.customer?.id,
              name: data?.registration.user?.firstName,
              fullName: `${data?.registration.user?.firstName || ""} ${
                data?.registration.user?.lastName || ""
              }`,
              createdAt: data?.registration.user?.createdAt,
            });
          }
          push('/dashboard');
        }
      },
      onError(error) {},
    });

  const isValidGoogleUser = useMemo(() => {
    if (!googleAuthUser) return false;
    if (googleAuthUser.email.toLocaleLowerCase() !== form.email.toLocaleLowerCase()) {
      setUnMatchedGoogleAccount(true);
      return false;
    }
    return true;
  }, [form.email, googleAuthUser]);

  const isValidMicrosoftUser = useMemo(() => {
    if (!microsoftUser) return false;
    if (microsoftUser.email.toLocaleLowerCase() !== form.email.toLocaleLowerCase()) {
      setUnMatchedMicrosoftAccount(true);
      return false;
    }
    return true;
  }, [form.email, microsoftUser]);

  const isValidCleverUser = useMemo(() => {
    if (!cleverUser) return false;
    if (cleverUser.email.toLocaleLowerCase() !== form.email.toLocaleLowerCase()) {
      setUnMatchedCleverAccount(true);
      return false;
    }
    return true;
  }, [form.email, cleverUser]);

  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      setFormSubmitting(true);
      setSocialAuthFlow(false);
      setUnMatchedGoogleAccount(false);
      setUnMatchedMicrosoftAccount(false);
      setUnMatchedCleverAccount(false);

      if (!hasError(formErrors)) {
        if (userId) {
          await registration({
            variables: {
              input: {
                id: userId,
                ...transformToAccountForm(form),
              },
            },
          });
        }
      }
    },
    [formErrors, userId, registration, form]
  );

  const handleSubmitWithGoogle = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      setUnMatchedGoogleAccount(false);

      if (
        !unMatchedGoogleAccount &&
        googleAuthUser &&
        form.acceptPolicy &&
        form.acceptTerm
      ) {
        if (userId) {
          setFormSubmitting(true);
          await registration({
            variables: {
              input: {
                id: userId,
                ...transformToAccountForm({
                  ...form,
                  firstName: googleAuthUser.given_name ?? form.firstName,
                  lastName: googleAuthUser.family_name ?? form.lastName,
                  password: generateRandomString(10),
                }),
              },
            },
          });
        }
      }
    },
    [unMatchedGoogleAccount, googleAuthUser, form, userId, registration]
  );

  const handleSubmitWithMicrosoft = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      setUnMatchedMicrosoftAccount(false);

      if (
        !unMatchedMicrosoftAccount &&
        microsoftUser &&
        form.acceptPolicy &&
        form.acceptTerm
      ) {
        if (userId) {
          setFormSubmitting(true);
          await registration({
            variables: {
              input: {
                id: userId,
                ...transformToAccountForm({
                  ...form,
                  firstName: form.firstName,
                  lastName: form.lastName,
                  password: generateRandomString(10),
                }),
              },
            },
          });
        }
      }
    },
    [unMatchedMicrosoftAccount, microsoftUser, form, userId, registration]
  );

  const handleSubmitWithClever = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      setUnMatchedCleverAccount(false);

      if (
        !unMatchedCleverAccount &&
        cleverUser &&
        form.acceptPolicy &&
        form.acceptTerm
      ) {
        if (userId) {
          setFormSubmitting(true);
          await registration({
            variables: {
              input: {
                id: userId,
                ...transformToAccountForm({
                  ...form,
                  firstName: cleverUser?.firstName ?? form.firstName,
                  lastName: cleverUser?.lastName ?? form.lastName,
                  password: generateRandomString(10),
                }),
              },
            },
          });
        }
      }
    },
    [unMatchedCleverAccount, cleverUser, form, userId, registration]
  );

  const onClickGoogleSignUp = useCallback(() => {
    setSocialAuthFlow(true);
    setUnMatchedGoogleAccount(false);
    googleAuth();
  }, [googleAuth]);

  const onClickMicrosoftSignUp = useCallback(() => {
    setSocialAuthFlow(true);
    setUnMatchedMicrosoftAccount(false);
    microsoftAuth();
  }, [microsoftAuth]);

  const onClickCleverSignUp = useCallback(() => {
    setSocialAuthFlow(true);
    setUnMatchedCleverAccount(false);
    cleverAuth();
  }, [cleverAuth]);

  const onFormSubmit = useCallback(
    (e: FormEvent) => {
      if (isValidGoogleUser) {
        handleSubmitWithGoogle(e);
      } else if (isValidMicrosoftUser) {
        handleSubmitWithMicrosoft(e);
      } else if (isValidCleverUser) {
        handleSubmitWithClever(e);
      } else {
        handleSubmit(e);
      }
    },
    [
      handleSubmit,
      handleSubmitWithGoogle,
      handleSubmitWithMicrosoft,
      handleSubmitWithClever,
      isValidGoogleUser,
      isValidMicrosoftUser,
      isValidCleverUser,
    ]
  );

  return (
    <RegistrationPageLayout
      loading={submitting || 
        invitedUserLoading ||
        googleAuthLoading ||
        microsoftAuthLoading ||
        cleverAuthLoading}
      onSubmit={onFormSubmit}
      buttonTitle={"Register"}
      title={"Registration"}
    >
      {(
        <div>
        <div className={styles.formContent}>
          {!isValidGoogleUser &&
            !isValidMicrosoftUser &&
            !isValidCleverUser && (
              <>
                <div>
                  <div className={styles.gWrapper}>
                    <div className={styles.titleView}>{'Register with Single Sign-On:'}</div>
                    <div
                      className={styles.gInput}
                      role="button"
                      aria-pressed="false"
                      onClick={onClickGoogleSignUp}
                    >
                      <div className={styles.iconBackground}>
                        <div className={styles.iconView}>
                          <GoogleLogoIcon />
                        </div>
                      </div>
                      <span className={styles.gLabel}>Register with Google</span>
                    </div>
                    <div
                      className={styles.msInput}
                      role="button"
                      aria-pressed="false"
                      onClick={onClickMicrosoftSignUp}
                    >
                      <div className={styles.iconBackground}>
                        <div className={styles.iconView}>
                          <MSWindowsLogoIcon width={18} />
                        </div>
                      </div>
                      <span className={styles.msLabel}>
                        Register with Microsoft
                      </span>
                    </div>
                    <div
                      className={styles.cleverInput}
                      role="button"
                      aria-pressed="false"
                      onClick={onClickCleverSignUp}
                    >
                      <div className={styles.iconBackground}>
                        <div className={styles.iconView}>
                          <CleverLogoIcon />
                        </div>
                      </div>
                      <span className={styles.cleverLabel}>
                        Register with Clever
                      </span>
                    </div>
                    {(unMatchedGoogleAccount ||
                    unMatchedMicrosoftAccount ||
                    unMatchedCleverAccount) && (
                      <div className={styles.eNoteBlock}>
                        <div className={styles.noteBlock_eLabel1}><strong>Note: </strong>{`Your ${
                          unMatchedGoogleAccount
                            ? "Google"
                            : unMatchedMicrosoftAccount
                            ? "Microsoft"
                            : unMatchedCleverAccount
                            ? "Clever"
                            : ""
                        } email must match:`}</div>
                        <div className={styles.noteBlock_eValue}>{form.email}</div>
                        <div className={styles.noteBlock_eLabel}>
                          If you think this is an error, please contact your Open
                          Parachute Administrator and ask them to update your email
                          address.
                        </div>
                      </div>
                    )}
                    {!isSocialAuthFlow && (
                      <div className={styles.noteBlock}>
                        <div className={styles.noteBlock_label}>
                          <strong>Note: </strong>Your email address must match:<br/> <strong>{form.email}</strong></div>
                      </div>
                    )}
                  </div>
                </div>

                <div className={styles.formWrapper}>
                  <div className={styles.titleView} style={{marginBottom: 20}}>{'Register with Email:'}</div>
                  <div className={styles.formField}>
                    <div className={styles.emailRow}>
                      <TextInput
                        allowSpecialCharacters
                        hasError={formSubmitting && !!formErrors["email"]}
                        subTitle={formSubmitting && formErrors["email"]}
                        value={form.email}
                        onChange={(val) => handleFieldChange("email", val)}
                        label={"Email *"}
                        id={"email"}
                        placeholder={"Email"}
                        readonly
                      />
                    </div>
                  </div>

                  <div className={styles.formField}>
                    <div className={styles.row}>
                      <TextInput
                        hasError={formSubmitting && !!formErrors["firstName"]}
                        subTitle={formSubmitting && formErrors["firstName"]}
                        value={form.firstName}
                        onChange={(val) => handleFieldChange("firstName", val)}
                        label={"First Name"}
                        id={"firstName"}
                        placeholder={"First Name"}
                        className={styles.userNameInput}
                      />
                      <TextInput
                        hasError={formSubmitting && !!formErrors["lastName"]}
                        subTitle={formSubmitting && formErrors["lastName"]}
                        value={form.lastName}
                        onChange={(val) => handleFieldChange("lastName", val)}
                        label={"Last Name"}
                        id={"lastName"}
                        placeholder={"Last Name"}
                        className={styles.userNameInput}
                      />
                    </div>
                  </div>
                  
                  <div className={styles.formField}>
                    <div className={styles.row}>
                      <TextInput
                        allowSpecialCharacters
                        hasError={formSubmitting && !!formErrors["password"]}
                        subTitle={formSubmitting ? formErrors["password"] : ''}
                        type="password"
                        value={form.password}
                        onChange={(val) => handleFieldChange("password", val)}
                        label={"Password *"}
                        id={"password"}
                        placeholder={"Password"}
                        className={styles.userNameInput}
                      />
                      <TextInput
                        allowSpecialCharacters
                        hasError={formSubmitting && !!formErrors["rePassword"]}
                        subTitle={formSubmitting ? formErrors["rePassword"] : ""}
                        type="password"
                        value={form.rePassword}
                        onChange={(val) => handleFieldChange("rePassword", val)}
                        label={"Retype Password *"}
                        id={"confirmPassword"}
                        placeholder={"Password"}
                        className={styles.userNameInput}
                      />
                    </div>
                  </div>
                  <div>
                  {
                    <span className={styles.passwordNote}>
                      Password should have at least <strong>one uppercase</strong> letter, <strong>one lowercase</strong> letter, <strong>one digit, one special character</strong> and be at least <strong>8 characters long.</strong>
                    </span>
                  }
                  </div>
                </div>
              </>
            )}
          {isValidGoogleUser && (
            <div>
              <div>You have successfully connected with Google:</div>
              <div className={styles.cNoteBlock}>
                <div className={styles.gcLabel}>
                  <div className={styles.inline}>
                    <div className={styles.rounded}>
                      <GoogleLogoIcon width={20} />
                    </div>
                    <span>
                      Connected with:&nbsp;&nbsp;&nbsp;
                      <span style={{ fontWeight: "500" }}>{form.email}</span>
                    </span>
                  </div>
                  <CheckMarkIcon />
                </div>
              </div>
            </div>
          )}
          {isValidMicrosoftUser && (
            <div>
              <div>You have successfully connected with Microsoft:</div>
              <div className={styles.cNoteBlock}>
                <div className={styles.gcLabel}>
                  <div className={styles.inline}>
                    <div className={styles.rounded}>
                      <MSWindowsLogoIcon width={20} />
                    </div>
                    <span>
                      Connected with:&nbsp;&nbsp;&nbsp;
                      <span style={{ fontWeight: "500" }}>{form.email}</span>
                    </span>
                  </div>
                  <CheckMarkIcon />
                </div>
              </div>
            </div>
          )}
          {isValidCleverUser && (
            <div>
              <div>You have successfully connected with Clever:</div>
              <div className={styles.cNoteBlock}>
                <div className={styles.gcLabel}>
                  <div className={styles.inline}>
                    <div className={styles.rounded}>
                      <CleverLogoIcon width={20} />
                    </div>
                    <span>
                      Connected with:&nbsp;&nbsp;&nbsp;
                      <span style={{ fontWeight: "500" }}>{form.email}</span>
                    </span>
                  </div>
                  <CheckMarkIcon />
                </div>
              </div>
            </div>
          )}
          <div className={styles.arrowDiv}>
            <span className={styles.arrowText}>
              Need help logging in?
              <br />
              Click to Chat with us!
            </span>
            <ArrowIcon width="40" height="200" />
          </div>
        </div>
        <div className={styles.touWrapper}>
          <div>
            <Checkbox
              id={"tou"}
              checked={form.acceptTerm}
              onChange={(val) => handleFieldChange("acceptTerm", val)}
            >
              <span className={styles.touText}>
                I have read and agree to the{" "}
                <a
                  className={styles.touLink}
                  onClick={() => {
                    window.open(
                      "https://openparachuteschools.com/terms-of-use",
                      "_blank"
                    );
                  }}
                  target="_blank"
                  rel="noreferrer"
                  href="https://openparachuteschools.com/terms-of-use"
                >
                  Terms of Use
                </a>
              </span>
            </Checkbox>
            {formSubmitting && !!formErrors["acceptTerm"] && (
              <div className={styles.serverError}>
                {formErrors["acceptTerm"]}
              </div>
            )}
          </div>
          <div>
            <Checkbox
              id={"policy"}
              checked={form.acceptPolicy}
              onChange={(val) => handleFieldChange("acceptPolicy", val)}
            >
              <span className={styles.touText}>
                I agree to the{" "}
                <a
                  className={styles.touLink}
                  onClick={() => {
                    window.open(
                      "https://openparachuteschools.com/privacy-policy/",
                      "_blank"
                    );
                  }}
                  target="_blank"
                  rel="noreferrer"
                  href="https://openparachuteschools.com/privacy-policy/"
                >
                  Privacy Policy
                </a>
              </span>
            </Checkbox>
            {formSubmitting && !!formErrors["acceptPolicy"] && (
              <div className={styles.serverError}>
                {formErrors["acceptPolicy"]}
              </div>
            )}
          </div>
        </div>
        {formSubmitting && !!registrationError?.message && (
          <div className={styles.serverError}>
            {registrationError.message}
          </div>
        )}
        </div>
      )}
    </RegistrationPageLayout>
  );
}
